import { css } from '@emotion/react';
import omit from 'lodash/omit';
import React, { useState, useContext, useCallback, useMemo, forwardRef, MutableRefObject, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import t from 'react-translate';
import moment from 'moment';

// schemas
import { CommunicationCategory, CommunicationType, ItemState, ScheduledForType } from 'redux/schemas/models/course-communication';
import { CourseAliases } from 'redux/schemas/models/course';

// contexts
import { CommunicationDispatch, TriggerType } from 'communications/course_communications/contexts/communication-context';

// selectors
import { getCourseAliases } from 'redux/selectors/course';
import { getItemState } from 'redux/selectors/course-communications';
import { isConfirmationDialogShown } from 'redux/selectors/confirmation-dialogs';

// components
import NvDropdown, { NvDropdownTextItem, NvDropdownButtonStyle } from 'shared/components/inputs/nv-dropdown';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvDatePicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import { NvPopover } from 'shared/components/nv-popover';
import ValidationErrorMessage from 'shared/components/inputs/validation-error-message';
import CommunicationBadge from 'shared/components/communication-badge';
import NvIcon from 'shared/components/nv-icon';
import DuplicateActivity from 'communications/course_communications/components/communication-modal/duplicate-activity';
import DuplicateLesson from 'communications/course_communications/components/communication-modal/duplicate-lesson';

// styles
import { gray3, gray7, info, primary } from 'styles/global_defaults/colors';
import { halfSpacing, standardSpacing, quarterSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';

type SendOnProps = {
  inactive?: boolean
  details?: string
  referenceDate?: string
};

type SendOnType = {
  title: string
  option: string
  type: 'string' | 'input' | 'datepicker'
  scheduledFor?: ScheduledForType
};

export const isPastDate = (referenceDate: string, scheduledFor: ScheduledForType, xDays: number) => {
  if (referenceDate) {
    const referenceDateMoment = moment(referenceDate);
    const momentNow = moment();

    switch (scheduledFor) {
      case ScheduledForType.BEFORE_DATE:
        return xDays && referenceDateMoment.subtract(xDays, 'days').isBefore(momentNow);
      case ScheduledForType.AFTER_DATE:
        return xDays && referenceDateMoment.add(xDays, 'days').isBefore(momentNow);
      case ScheduledForType.ON_DATE:
      default:
        return referenceDateMoment.isBefore(momentNow);
    }
  }

  return false;
};

const getItems = (type: CommunicationType, aliases: CourseAliases): SendOnType[] => {
  switch (type) {
    case CommunicationType.COMPLETED:
      return [
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.COMPLETE_ACTIVITY.IMMEDIATELY.TITLE(aliases.learnersAliases),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.COMPLETE_ACTIVITY.IMMEDIATELY.OPTION(),
          type: 'string',
          scheduledFor: ScheduledForType.ON_DATE,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.COMPLETE_ACTIVITY.DAYS_AFTER.TITLE({ input: 'INPUT', ...aliases.learnersAliases }),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.COMPLETE_ACTIVITY.DAYS_AFTER.OPTION(),
          type: 'input',
          scheduledFor: ScheduledForType.AFTER_DATE,
        },
      ];
    case CommunicationType.NOT_COMPLETED:
      return [
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.DONE_FOLLOWING.IMMEDIATELY.TITLE(aliases.learnersAliases),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.DONE_FOLLOWING.IMMEDIATELY.OPTION(),
          type: 'string',
          scheduledFor: ScheduledForType.ON_DATE,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.DONE_FOLLOWING.DAYS_AFTER.TITLE({ input: 'INPUT', ...aliases.learnersAliases }),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.DONE_FOLLOWING.DAYS_AFTER.OPTION(),
          type: 'input',
          scheduledFor: ScheduledForType.AFTER_DATE,
        },
      ];
    case CommunicationType.DUE_DATE_EMAIL:
    case CommunicationType.DUE_DATE_ANNOUNCEMENT:
      return [
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.DUE_DATE.BEFORE.TITLE('INPUT'),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.DUE_DATE.BEFORE.OPTION(),
          type: 'input',
          scheduledFor: ScheduledForType.BEFORE_DATE,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.DUE_DATE.ON.TITLE(),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.DUE_DATE.ON.OPTION(),
          type: 'string',
          scheduledFor: ScheduledForType.ON_DATE,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.DUE_DATE.AFTER.TITLE('INPUT'),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.DUE_DATE.AFTER.OPTION(),
          type: 'input',
          scheduledFor: ScheduledForType.AFTER_DATE,
        },
      ];
    case CommunicationType.RELEASE_DATE_EMAIL:
    case CommunicationType.RELEASE_DATE_ANNOUNCEMENT:
      return [
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.RELEASE_DATE.ON.TITLE(),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.RELEASE_DATE.ON.OPTION(),
          type: 'string',
          scheduledFor: ScheduledForType.ON_DATE,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.RELEASE_DATE.AFTER.TITLE('INPUT'),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.RELEASE_DATE.AFTER.OPTION(),
          type: 'input',
          scheduledFor: ScheduledForType.AFTER_DATE,
        },
      ];
    case CommunicationType.MANUAL_EMAIL:
    case CommunicationType.MANUAL_ANNOUNCEMENT:
      return [
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.NOW.TITLE(),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.NOW.OPTION(),
          type: 'string',
          scheduledFor: ScheduledForType.SEND_NOW,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.LATER.TITLE(),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.LATER.OPTION(),
          type: 'datepicker',
          scheduledFor: ScheduledForType.SCHEDULED,
        },
      ];
    default:
      return [
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.NOW.TITLE(),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.NOW.OPTION(),
          type: 'string',
          scheduledFor: ScheduledForType.SEND_NOW,
        },
        {
          title: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.LATER.TITLE(),
          option: t.COURSE_COMMUNICATIONS.SEND_ON.CUSTOM.LATER.OPTION(),
          type: 'datepicker',
        },
      ];
  }
};

const GetTitle = forwardRef((props: {
  item: SendOnType,
  canShowTriggerWarning: boolean
  isDisabled: boolean,
}, ref: MutableRefObject<HTMLInputElement>) => {
  const { item } = props;

  const [showTriggerTimeWarning, setShowTriggerTimeWarning] = useState(false);

  const styles = css`
    &.custom-target {
      display: flex;
      align-items: center;
      color: ${props.isDisabled ? gray3 : primary};

      & > * {
        display: inline-block;
      }

      .icon {
        display: flex;
        align-items: center;
      }
    }
  `;

  const onInputPropsChange = (e) => {
    const { target: { value }, type } = e;
    const showPopOver = !!value && type === 'blur';

    setShowTriggerTimeWarning(showPopOver);
  };

  if (item.type === 'input') {
    const [prefix, suffix] = item.title.split('INPUT');

    return (
      <div className='custom-target text-large-regular bold' css={styles}>
        {prefix}
        <NvPopover
          show={showTriggerTimeWarning && props.canShowTriggerWarning}
          placement='top'
          preventOverflow={false}
          content={(
            <ValidationErrorMessage
              title={t.COURSE_COMMUNICATIONS.SEND_ON.WARNING.TITLE()}
              text={t.COURSE_COMMUNICATIONS.SEND_ON.WARNING.NOTE()}
            />
          )}
        >
          <NvTextInput
            withForm
            required
            type='number'
            autoComplete='off'
            name='xDays'
            ariaLabel={t.COURSE_COMMUNICATIONS.SEND_ON.ARIA_LABEL()}
            inputClassName='text-large-regular'
            onBlur={(e) => onInputPropsChange(e)}
            onFocus={(e) => onInputPropsChange(e)}
            onChange={(e) => {
              setShowTriggerTimeWarning(!!Number(e.target.value));
            }}
            ref={ref}
          />
        </NvPopover>
        {suffix}
        <div className='icon text-xs icon-dropdown-arrow ml-2' />
      </div>
    );
  }

  return (
    <div className='custom-target' css={styles}>
      <div className='text-large-regular bold'>{item.title}</div>
      <div className='icon text-xs icon-dropdown-arrow ml-2' />
    </div>
  );
});

export const SendOn = (props: SendOnProps) => {
  const styles = (isDisabled: boolean) => css`
    &.send-on {
      padding: ${standardSpacing}px;
      background-color: ${info};
      border-radius: 10px;

      .nv-text-input {
        display: inline-block;
        width: ${tripleSpacing}px;
        margin-left: ${quarterSpacing}px;
        margin-right: ${quarterSpacing}px;
      }

      .details {
        margin-top: ${halfSpacing}px;
      }

      .send-on-date {
        display: inline-block;
        margin-left: ${standardSpacing}px;

        .nv-text-input {
          width: 250px;
          margin: auto;
        }

        input[readonly] {
          background-color: ${gray7};
        }
      }
      .badge-wrapper {
        margin: -${standardSpacing}px
      }
    }

    .edit-icon {
      cursor: pointer;

      .icon {
        display: inline;
      }
    }

    .send-on-dropdown-wrapper {
      display: flex;
      align-items: center;
      ${isDisabled && 'pointer-events:none;'}
    }
  `;

  const { State } = useContext(CommunicationDispatch);

  const aliases = useSelector((state) => getCourseAliases(state));
  const itemState = useSelector((state) => getItemState(state, State.communicationId));
  const isConfirmationOverlayShown = useSelector((state) => isConfirmationDialogShown(state));

  const { control, setValue, trigger, watch } = useFormContext();
  const [scheduledFor, xDays, scheduledOn] = watch(['scheduledFor', 'xDays', 'scheduledOn']);

  const [showPastDatePopover, setShowPastDatePopover] = useState(false);

  const items = useMemo(() => getItems(State.communicationType, aliases), [State.communicationType, aliases]);
  const initialIndex = items.findIndex((item) => item.scheduledFor === (scheduledFor
    ?? ScheduledForType.ON_DATE));

  const selected = useMemo(() => items[initialIndex], [items, initialIndex]);
  const ref = useRef<HTMLInputElement>();

  const [isEditing, setEditing] = useState(false);

  const showStatusBadge = State.triggerType === TriggerType.EDIT && itemState === ItemState.ACTIVATED;
  const isDisabled = State.communicationCategory === CommunicationCategory.SCHEDULED_ANNOUNCEMENT
    && itemState === ItemState.SENT
    && State.triggerType === TriggerType.EDIT;

  // The warning popover will be enabled when state is not Draft/Error and there is no overlay
  const canShowTriggerWarning = State.triggerType === TriggerType.EDIT
    && (State.communicationType === CommunicationType.COMPLETED
      || State.communicationType === CommunicationType.NOT_COMPLETED) // warning is only for triggers
    && itemState
    && itemState !== ItemState.DRAFT
    && itemState !== ItemState.ERROR && !isConfirmationOverlayShown;

  const onSelect = (item) => {
    setValue('scheduledFor', item.scheduledFor, { shouldValidate: true, shouldDirty: true });

    if (item.type === 'input' || item.type === 'datepicker') {
      setTimeout(() => {
        ref.current?.focus();
        trigger('scheduledFor');
      });
    }
  };

  const dropdownData: NvDropdownTextItem[] = items.map((item) => ({
    type: 'text',
    text: item.option,
    callback: () => onSelect(item),
  }));

  const customTarget = useCallback(
    (titleProps) => (
      <GetTitle
        {...titleProps}
        item={selected}
        ref={ref}
        canShowTriggerWarning={canShowTriggerWarning}
        isDisabled={isDisabled}
      />
    ),
    [selected, canShowTriggerWarning, isDisabled],
  );

  const showPastDateWarning = isPastDate(props.referenceDate, scheduledFor, xDays)
    && !isDisabled; // No need to display when sendon disabled
  const [prefix, suffix] = props.details ? props.details.split('EDIT_ICON') : [];

  const getEntityDropdown = (isEditingEntity: boolean) => {
    if (State.communicationType === CommunicationType.DUE_DATE_EMAIL
      || State.communicationType === CommunicationType.DUE_DATE_ANNOUNCEMENT) {
      return <DuplicateActivity isDisplayed={isEditingEntity} onClose={() => setEditing(false)} />;
    }

    if (State.communicationType === CommunicationType.RELEASE_DATE_EMAIL
      || State.communicationType === CommunicationType.RELEASE_DATE_ANNOUNCEMENT) {
      return <DuplicateLesson isDisplayed={isEditingEntity} onClose={() => setEditing(false)} />;
    }
    return null;
  };

  return (
    <div css={styles(isDisabled)} className='send-on'>
      {showStatusBadge && <CommunicationBadge communicationId={State.communicationId} />}
      <div className='send-on-dropdown-wrapper'>
        <Controller
          name='scheduledFor'
          control={control}
          render={({ field }) => (
            <NvDropdown
              showSelectedIndicator
              items={dropdownData}
              buttonStyle={NvDropdownButtonStyle.CUSTOM}
              customTarget={customTarget}
              initialIndex={initialIndex}
              {...omit(field, ['ref'])}
            />
          )}
        />
        {selected?.type === 'datepicker' && (
          <NvDatePicker
            withForm
            required
            name='scheduledOn'
            type={DatePickerType.DATETIME}
            placeholder={t.FORM.PICK_DATE_TIME()}
            placement='top-start'
            className='send-on-date'
            min={moment()}
          />
        )}
      </div>
      {props.details && (
      <React.Fragment>
        {!isEditing && (
          <div className='mt-2'>
            {State.triggerType === TriggerType.DUPLICATE ? (
              <React.Fragment>
                <span onClick={() => setEditing(true)} className='text-primary'>
                  {prefix}
                  <span className='mx-1 edit-icon'>
                    <NvIcon size='xss-smallest' icon='edit' />
                  </span>
                </span>
                {suffix}
              </React.Fragment>
            ) : <span>{prefix + suffix}</span> }
            {showPastDateWarning ? (
              <NvPopover
                show={showPastDatePopover}
                placement='top'
                className='inline-block'
                preventOverflow={false}
                content={(
                  <ValidationErrorMessage
                    title={t.COURSE_COMMUNICATIONS.SEND_ON.PAST_DATE.TITLE()}
                    text={t.COURSE_COMMUNICATIONS.SEND_ON.PAST_DATE.NOTE()}
                  />
                )}
              >
                <div className='text-danger ml-1'>
                  <div
                    className='icon icon-xss-smallest icon-warning'
                    onMouseEnter={() => setShowPastDatePopover(true)}
                    onMouseLeave={() => setShowPastDatePopover(false)}
                  />
                </div>
              </NvPopover>
            ) : null}
          </div>
        )}
        {getEntityDropdown(isEditing)}
      </React.Fragment>
      )}
    </div>
  );
};

export default SendOn;
